/**
 * Named boss roster (HEX-125).
 *
 * Five thematically distinct bosses — bruiser, poisoner, glass-cannon, fortress-
 * defender, and entropy-clock — spanning all four enemy tiers. Identity (name,
 * glyph, deck) is authored here; HP is distance-driven in `buildBossTemplate`
 * (bossEncounter.ts) via `bossTierFor`.
 *
 * Adding a boss: one new template constant + one entry in `BOSS_ROSTER`.
 */

import { EXECUTIONER } from '../combat/cards/attack/executioner';
import { JAB } from '../combat/cards/attack/jab';
import { POWER_STRIKE } from '../combat/cards/attack/power-strike';
import { VENOMBLADE } from '../combat/cards/attack/venomblade';
import { WORLDBREAKER } from '../combat/cards/attack/worldbreaker';
import { AEGIS } from '../combat/cards/defense/aegis';
import { FORTRESS } from '../combat/cards/defense/fortress';
import { BLOODLETTING } from '../combat/cards/recursive/bloodletting';
import { ECHOES } from '../combat/cards/recursive/echoes';
import { ENFEEBLE } from '../combat/cards/support/enfeeble';
import { PLAGUE } from '../combat/cards/support/plague';
import { TOXIC_DART } from '../combat/cards/support/toxic-dart';
import { VENOM } from '../combat/cards/support/venom';
import { COLD_SNAP } from '../combat/cards/trap/cold-snap';
import { DOOMSDAY_CLOCK } from '../combat/cards/trap/doomsday-clock';
import { MIRROR } from '../combat/cards/trap/mirror';
import type { EnemyTemplate } from '../combat/enemies';
import type { Biome } from '../core/tile';

/**
 * Bruiser — raw melee damage, no tricks. Common tier: 2×2 grid.
 * The greedy AI funds Power Strike first; Jab fills the spare die.
 * No defence — a wall of muscle that wants to end the fight fast.
 */
const GROGNAKK: EnemyTemplate = {
  id: 'boss:grognakk-the-crusher',
  name: 'Grognakk the Crusher',
  glyph: '♟', // foot-soldier pawn — a brute in crude armour
  tier: 'common',
  hp: 14,
  loadout: [
    { card: POWER_STRIKE, origin: { row: 0, col: 0 }, rotated: false },
    { card: JAB, origin: { row: 1, col: 0 }, rotated: false },
  ],
  drops: { gold: 15 },
};

/**
 * Poisoner — layered DoT and debuffs. Rare tier: 3×3 grid.
 * Plague and Venom stack poison fast; Enfeeble reduces your counter-damage;
 * Toxic Dart tops off a stack on a cheap die. Kills through attrition.
 */
const SYLVA: EnemyTemplate = {
  id: 'boss:sylva-the-viper-queen',
  name: 'Sylva the Viper Queen',
  glyph: '♝', // bishop — diagonal, cunning, always flanking
  tier: 'rare',
  hp: 28,
  loadout: [
    { card: PLAGUE, origin: { row: 0, col: 0 }, rotated: false },
    { card: TOXIC_DART, origin: { row: 0, col: 2 }, rotated: false },
    { card: VENOM, origin: { row: 1, col: 0 }, rotated: false },
    { card: ENFEEBLE, origin: { row: 1, col: 1 }, rotated: false },
  ],
  drops: { gold: 22 },
};

/**
 * Glass cannon — self-immolates for burst damage. Epic tier: 4×3 grid.
 * Venomblade deals 8 + poison; Executioner (rotated to 1×2 column) spikes
 * for 14; Bloodletting pays 3 HP for a 3-die explosive roll; Echoes (rotated)
 * fires 3 then flips for 3 more. No defence — race it or be raced.
 */
const CINDER_WYRM: EnemyTemplate = {
  id: 'boss:cinder-wyrm',
  name: 'Cinder Wyrm',
  glyph: '♃', // Jupiter — ancient symbol of fire, scale, and overwhelming force
  tier: 'epic',
  hp: 40,
  loadout: [
    { card: VENOMBLADE, origin: { row: 0, col: 0 }, rotated: false },
    { card: EXECUTIONER, origin: { row: 0, col: 2 }, rotated: true },
    { card: BLOODLETTING, origin: { row: 2, col: 0 }, rotated: false },
    { card: ECHOES, origin: { row: 2, col: 2 }, rotated: true },
  ],
  drops: { gold: 35 },
};

/**
 * Fortress defender — maximum block, debuffs, status reflection. Legendary: 4×4.
 * Fortress walls for 20; Aegis adds 12 block + Weaken; Enfeeble stacks further
 * Weaken; Mirror rebounds any status you apply. Outlasts rather than outpaces.
 */
const GRANTEEL: EnemyTemplate = {
  id: 'boss:granteel-the-bulwark',
  name: 'Granteel the Bulwark',
  glyph: '♕', // queen — the supreme defender, holds every angle
  tier: 'legendary',
  hp: 58,
  loadout: [
    { card: FORTRESS, origin: { row: 0, col: 0 }, rotated: false },
    { card: ENFEEBLE, origin: { row: 0, col: 3 }, rotated: false },
    { card: AEGIS, origin: { row: 2, col: 0 }, rotated: false },
    { card: MIRROR, origin: { row: 2, col: 2 }, rotated: false },
  ],
  drops: { gold: 60 },
};

/**
 * Entropy clock — Doomsday tick races the player; Worldbreaker ends it.
 * Legendary: 4×4 grid.
 * Clock charges each turn and detonates for 30 to both sides at charge 5 —
 * a shared death sentence. Worldbreaker swings for 20 in the meantime;
 * Cold Snap freezes your best card on a 1-roll; Mirror rebounds status.
 * Kill it before the Clock decides, or survive the blast.
 */
const THE_PALE_HOLLOW: EnemyTemplate = {
  id: 'boss:the-pale-hollow',
  name: 'The Pale Hollow',
  glyph: '☾', // crescent moon — pale, ghostly, its end is also yours
  tier: 'legendary',
  hp: 55,
  loadout: [
    { card: WORLDBREAKER, origin: { row: 0, col: 0 }, rotated: false },
    { card: JAB, origin: { row: 0, col: 3 }, rotated: false },
    { card: COLD_SNAP, origin: { row: 1, col: 3 }, rotated: false },
    { card: DOOMSDAY_CLOCK, origin: { row: 2, col: 0 }, rotated: false },
    { card: MIRROR, origin: { row: 2, col: 2 }, rotated: false },
  ],
  drops: { gold: 60 },
};

/** All named bosses, in roster order. Index is stable — do not reorder. */
export const BOSS_ROSTER: readonly EnemyTemplate[] = [
  GROGNAKK,
  SYLVA,
  CINDER_WYRM,
  GRANTEEL,
  THE_PALE_HOLLOW,
];

/** Biome ordering used by `selectBoss` for stable index mixing. */
const BIOMES: readonly Biome[] = ['plain', 'forest', 'snow', 'lake', 'mountain'];

/**
 * Pick a named boss deterministically from the roster.
 *
 * `townSeed` is a spatial hash of the town tile's coordinates; `biome` adds a
 * second axis so two adjacent towns of different biomes can host different
 * bosses. Same `(townSeed, biome)` always returns the same boss.
 */
export function selectBoss(townSeed: number, biome: Biome): EnemyTemplate {
  const biomeIdx = BIOMES.indexOf(biome);
  // 32-bit Murmur3-like finaliser — mixes townSeed and biome into a stable index.
  let h = (townSeed + biomeIdx * 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  const idx = h % BOSS_ROSTER.length;
  return BOSS_ROSTER[idx] ?? (BOSS_ROSTER[0] as EnemyTemplate);
}
